import {
    ClassNames,
    DateRange,
    DayPicker,
    DayPickerProps,
    getDefaultClassNames as defaultDayPickerClassNames,
    WeekNumber,
    WeekNumberHeader,
} from '@daypicker/react'
import { nb } from '@daypicker/react/locale'
import { formatDate } from 'astra'

import { SizeOptions } from '../../../../types/types'
import { astraTwMerge } from '../../../Utilities/astraTwMerge'
import { AstraDayPickerDay } from './components/AstraDayPickerDay'
import { AstraDayPickerDayButton } from './components/AstraDayPickerDayButton'
import { AstraDayPickerWeek } from './components/AstraDayPickerWeek'
import { AstraDayPickerWeekday } from './components/AstraDayPickerWeekday'
import { DefaultFooter } from './components/DefaultFooter'
import { TodayButtonProps } from './components/TodayButton'
import { useDaySelectionHelpers } from './hooks/useDaySelectionHelpers'
import { useMonthControl } from './hooks/useMonthControl'

export type AstraDayPickerProps = DayPickerProps &
    TodayButtonProps & {
        /**
         * Determines if the calendar should only show weekdays
         */
        weekdaysOnly?: boolean
        /**
         * Prevents the same ISO week from appearing in multiple displayed months.
         */
        hideDuplicateWeeks?: boolean
        /**
         * Tooltip text for specified dates
         */
        tooltips?: { date: Date; text: string }[]
        /**
         * Sizes
         */
        size?: SizeOptions
        /**
         * Should the calendar take all available width?
         */
        fullWidth?: boolean

        /**
         * Renders a "Fjern" button in the footer if present
         */
        onClearClick?: () => void
    }

type AstraDayPickerRestProps = Omit<
    AstraDayPickerProps,
    'weekdaysOnly' | 'showOutsideDays' | 'hideDuplicateWeeks' | 'size' | 'fullWidth' | 'classNames' | 'month' | 'onMonthChange' | 'onClearClick'
>

type RangeModeAstraDayPickerProps = AstraDayPickerRestProps & {
    mode: 'range'
    selected?: DateRange
}

function isRangeMode(props: AstraDayPickerRestProps): props is RangeModeAstraDayPickerProps {
    return props.mode === 'range'
}

const rootBaseStyles = 'relative p-4'
const chevronBaseStyles = 'p-0.5'
const monthsBaseStyles = 'flex justify-between gap-4'
const monthGridStyles = 'border-collapse border-spacing-0'
const navStyles = 'absolute z-elevated top-4 right-2 flex gap-4 px-2 pb-4 text-default'
const monthStyles = 'capitalize grow'
const captionLabelStyles = 'font-medium'
const monthCaptionStyles = 'px-2 pb-5 text-lg'
const footerStyles = 'pt-4'

const dayButtonBaseStyles = 'flex h-full w-full items-center justify-center disabled:opacity-15 cursor-pointer disabled:cursor-not-allowed group/day-button'
const weekdayStyles = 'font-normal pb-3 text-center'
const weekStyles = '[&:first-child>*:first-child>*]:rounded-t-md [&:last-child>*:first-child>*]:rounded-b-md'
const weekNumberHeaderStyles = 'pb-3 font-normal text-muted'
const weekNumberStyles = 'font-normal p-2 text-muted bg-surface-muted'

const selectedStyles = 'selected'
const rangeStartStyles = 'range-start'
const rangeMiddleStyles = 'range-middle'
const rangeEndStyles = 'range-end'
const hoverRangeStyles = 'hover-range'
const hoverRangeStartStyles = 'hover-range-start'
const hoverRangeEndStyles = 'hover-range-end'

/**
 * A date picker component that allows the user to select a single day, multiple days, or a range of days.
 */
export const AstraDayPicker = ({
    weekdaysOnly,
    hideDuplicateWeeks,
    showOutsideDays,
    size = 'medium',
    fullWidth,
    classNames,
    month,
    onMonthChange,
    onClearClick,
    showWeekNumber = true,
    ...rest
}: AstraDayPickerProps) => {
    const selected = isRangeMode(rest) ? rest.selected : undefined
    const { clearHoverRange, handleDayMouseEnter, modifiers } = useDaySelectionHelpers(rest.mode, selected, rest.disabled)
    const { handleOnMonthChange, monthSelected } = useMonthControl(month, onMonthChange)

    function formatWeekdayName(date: Date): string {
        const weekday = formatDate(date, 'dd')
        return `${weekday.at(0)?.toLocaleUpperCase() ?? ''}${weekday.at(1) ?? ''}`
    }

    function formatWeekNumber(weekNumber: number): string {
        return `${weekNumber}`
    }

    function getDefaultClassNames() {
        const defaultClassNames = defaultDayPickerClassNames()

        return {
            months: astraTwMerge(monthsBaseStyles, fullWidth ? 'w-full' : ''),
            selected: selectedStyles,
            root: astraTwMerge(defaultClassNames.root, rootBaseStyles, fullWidth ? 'w-full' : 'w-fit'),
            chevron: astraTwMerge(defaultClassNames.chevron, chevronBaseStyles),
            day_button: astraTwMerge(defaultClassNames.day_button, dayButtonBaseStyles),
            nav: navStyles,
            month_grid: astraTwMerge(monthGridStyles, fullWidth ? 'w-full' : ''),
            month: monthStyles,
            week: weekStyles,
            week_number: weekNumberStyles,
            week_number_header: weekNumberHeaderStyles,
            caption_label: captionLabelStyles,
            month_caption: monthCaptionStyles,
            weekday: weekdayStyles,
            range_start: rangeStartStyles,
            range_middle: rangeMiddleStyles,
            range_end: rangeEndStyles,
            footer: footerStyles,
        } satisfies Partial<ClassNames>
    }

    function getDefaultModifiersClassNames() {
        return {
            hoverRange: hoverRangeStyles,
            hoverRangeStart: hoverRangeStartStyles,
            hoverRangeEnd: hoverRangeEndStyles,
        }
    }

    return (
        <DayPicker
            {...rest}
            showWeekNumber={showWeekNumber}
            ISOWeek={hideDuplicateWeeks || rest.ISOWeek}
            locale={nb}
            showOutsideDays={showOutsideDays}
            formatters={{ formatWeekdayName, formatWeekNumber }}
            modifiers={{
                ...modifiers,
                ...rest.modifiers,
            }}
            modifiersClassNames={{
                ...getDefaultModifiersClassNames(),
                ...rest.modifiersClassNames,
            }}
            classNames={{
                ...getDefaultClassNames(),
                ...classNames,
            }}
            month={monthSelected}
            onMonthChange={handleOnMonthChange}
            onDayMouseEnter={(date, dayModifiers, event) => {
                handleDayMouseEnter(date)
                rest.onDayMouseEnter?.(date, dayModifiers, event)
            }}
            onDayMouseLeave={(date, dayModifiers, event) => {
                clearHoverRange()
                rest.onDayMouseLeave?.(date, dayModifiers, event)
            }}
            components={{
                Day: props => (
                    <AstraDayPickerDay
                        {...props}
                        showOutsideDays={showOutsideDays}
                        fixedWeeks={rest.fixedWeeks}
                        weekdaysOnly={weekdaysOnly}
                        tooltips={rest.tooltips}
                    />
                ),
                DayButton: props => <AstraDayPickerDayButton {...props} size={size} />,
                WeekNumber: ({ children, className, ...props }) => (
                    <WeekNumber {...props} className="p-0 px-1">
                        <div className={className}>{children}</div>
                    </WeekNumber>
                ),
                Weekday: props => <AstraDayPickerWeekday {...props} weekdaysOnly={weekdaysOnly} />,
                WeekNumberHeader: props => <WeekNumberHeader {...props}>Uke</WeekNumberHeader>,
                Week: props => (
                    <AstraDayPickerWeek
                        {...props}
                        hideDuplicateWeeks={hideDuplicateWeeks}
                        weekdaysOnly={weekdaysOnly}
                        showOutsideDays={showOutsideDays}
                        fixedWeeks={rest.fixedWeeks}
                    />
                ),
                ...rest.components,
            }}
            footer={rest.footer ?? <DefaultFooter {...rest} onMonthChange={handleOnMonthChange} onClearClick={onClearClick} />}
        />
    )
}
